import React from 'react';
import { SectionCard } from '../../common/SectionCard';
import { EmptyState } from '../../common/EmptyState';
import { SeverityPill } from '../../common/StatusPills';
import { Progress } from '../../ui/Progress';
import { formatAed, formatDate } from '../../../utils/format';
import { cn } from '../../../utils/cn';
import type { PlanBuildDetail } from '../../../types/domain';
const workstreamClasses = {
  'on-track': 'text-uae-leaf',
  'at-risk': 'text-uae-bronze',
  delayed: 'text-uae-red'
};
const milestoneClasses = {
  done: 'border-uae-leaf/30 bg-uae-leaf/10 text-uae-leaf',
  due: 'border-border bg-muted text-foreground',
  overdue: 'border-uae-red/30 bg-uae-red/10 text-uae-red'
};
export function PlanBuildPanel({
  planBuild
}: {
  planBuild?: PlanBuildDetail;
}) {
  if (!planBuild) {
    return <EmptyState title="Delivery baseline not established" description="Workstreams, schedule and budget draw-down appear once the record clears the Approval gate and the baseline is set." data-id="element-941" data-mp="197dhy960eiak" />;
  }
  const drawnPercent = Math.round(planBuild.budgetDrawnAed / planBuild.budgetMandateAed * 100);
  const meanComplete = Math.round(planBuild.workstreams.reduce((sum, ws) => sum + ws.complete, 0) / planBuild.workstreams.length);
  return <div className="space-y-4" data-id="element-942" data-mp="31d3wffd9llkr">
      <div className="grid gap-4 lg:grid-cols-3" data-id="element-943" data-mp="31e5ieevtjsh9">
        <SectionCard title="Budget draw-down" data-id="element-944" data-mp="36cgpwiyn5qwb">
          <p className="font-mono text-xl font-semibold" data-id="element-945" data-mp="36bovvckp0185">
            {formatAed(planBuild.budgetDrawnAed)}
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-946" data-mp="3ac8teh96h6ut">
            {drawnPercent}% of {formatAed(planBuild.budgetMandateAed)} mandate
          </p>
          <div className="mt-2" data-id="element-947" data-mp="3kchsc2uk16dw">
            <Progress value={drawnPercent} data-id="element-948" data-mp="3kbgg3v1sydul" />
          </div>
        </SectionCard>
        <SectionCard title="Schedule completion" data-id="element-949" data-mp="1l0faex1kwk2c">
          <p className="font-mono text-xl font-semibold" data-id="element-950" data-mp="1py73hudymc84">{meanComplete}%</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-951" data-mp="1q0b4o4kh4x4p">
            Mean completion across {planBuild.workstreams.length} workstreams
          </p>
          <div className="mt-2" data-id="element-952" data-mp="1dwz18ihjvsn8">
            <Progress value={meanComplete} data-id="element-953" data-mp="1dxqjkrm4ztvk" />
          </div>
        </SectionCard>
        <SectionCard title="Baseline" data-id="element-954" data-mp="1rwhw49lcp7hl">
          <p className="text-xl font-semibold" data-id="element-955" data-mp="1rtc9r243wjpk">
            {planBuild.baselineApproved ? 'Approved' : 'Not approved'}
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-956" data-mp="20vlh5j42vwyq">
            {drawnPercent > meanComplete + 8 ? 'Draw-down is running ahead of completion — cost-to-complete review recommended.' : 'Draw-down is tracking within tolerance of completion.'}
          </p>
        </SectionCard>
      </div>

      <SectionCard title="Workstreams" description="Owners and completion against the approved baseline" bodyClassName="p-0" data-id="element-957" data-mp="25nn17r7hmrzb">
        <ul className="divide-y divide-border" data-id="element-958" data-mp="2flt1k4dnbq22">
          {planBuild.workstreams.map(workstream => <li key={workstream.id} className="px-4 py-3" data-id="element-959" data-mp="2z6jk4r5bq2jx">
              <div className="flex items-center justify-between gap-3" data-id="element-960" data-mp="2z4ypl1j0ejm2">
                <div className="min-w-0" data-id="element-961" data-mp="3384qdtncril5">
                  <p className="truncate text-[13px] font-medium" data-id="element-962" data-mp="2p8vl3p0oq8vy">{workstream.name}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-963" data-mp="2pb9dfq3zd6zt">{workstream.owner}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3" data-id="element-964" data-mp="2fj3qiusl5frh">
                  <span className={cn('text-[11px] capitalize', workstreamClasses[workstream.status])} data-id="element-965" data-mp="2fhsmquz5480d">
                    {workstream.status.replace('-', ' ')}
                  </span>
                  <span className="font-mono text-[12px]" data-id="element-966" data-mp="2bi06dw3i8xkg">{workstream.complete}%</span>
                </div>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted" data-id="element-967" data-mp="1wmkbjov3oxn0">
                <div className={cn('h-full rounded-full', workstream.status === 'delayed' ? 'bg-uae-red' : workstream.status === 'at-risk' ? 'bg-uae-gold' : 'bg-uae-green')} style={{
              width: `${workstream.complete}%`
            }} data-id="element-968" data-mp="21lf0l8jnvifk" />
              </div>
            </li>)}
        </ul>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2" data-id="element-969" data-mp="2l3wbcqo4ujna">
        <SectionCard title="Master schedule milestones" bodyClassName="p-0" data-id="element-970" data-mp="2h3kqbcruyhba">
          <ul className="divide-y divide-border" data-id="element-971" data-mp="2c513791j2zhc">
            {planBuild.milestones.map(milestone => <li key={milestone.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-972" data-mp="2779eenw6jthd">
                <div className="min-w-0" data-id="element-973" data-mp="ac3hdx66575w">
                  <p className="truncate text-[13px]" data-id="element-974" data-mp="5el9ma3w0xzh">{milestone.label}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-975" data-mp="cym24eot11xx">
                    Due {formatDate(milestone.due)}
                  </p>
                </div>
                <span className={cn('shrink-0 rounded-sm border px-1.5 py-0.5 text-[11px] capitalize', milestoneClasses[milestone.status])} data-id="element-976" data-mp="hw4d0amdtr8k">
                  {milestone.status}
                </span>
              </li>)}
          </ul>
        </SectionCard>

        <SectionCard title="Risk register" bodyClassName="p-0" data-id="element-977" data-mp="3v3fj6hdadgva">
          <ul className="divide-y divide-border" data-id="element-978" data-mp="3v61yj36f50o2">
            {planBuild.risks.map(risk => <li key={risk.id} className="px-4 py-3" data-id="element-979" data-mp="40acofzoca72">
                <div className="flex items-start justify-between gap-3" data-id="element-980" data-mp="8xsbhdjz991c">
                  <p className="text-[13px] font-medium leading-snug" data-id="element-981" data-mp="1fdmd0gw86aoh">{risk.label}</p>
                  <SeverityPill severity={risk.severity} data-id="element-982" data-mp="1kbxk4jv4y730" />
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-983" data-mp="1ke0knxtwzilz">Owner · {risk.owner}</p>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground" data-id="element-984" data-mp="3gh58z4lq9irs">
                  {risk.mitigation}
                </p>
              </li>)}
          </ul>
        </SectionCard>
      </div>
    </div>;
}